import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import LoginPage from "./components/LoginPage";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import EmpresasPage from "./components/EmpresasPage";
import TareasPage from "./components/TareasPage";
import EmbudosPage from "./components/EmbudosPage";
import EvaluacionesPage from "./components/EvaluacionesPage";
import ReportsPage from "./components/ReportsPage";
import AdminPage from "./components/AdminPage";
import GoogleToolsPage from "./components/GoogleToolsPage";
import PublicFormsPage from "./components/PublicFormsPage";
import PublicFormRenderer from "./components/PublicFormRenderer";
import MarketingPage from "./components/MarketingPage";
import AgentsPage from "./components/AgentsPage";
import AutomationsPage from "./components/AutomationsPage";
import GmailPanel from "./components/GmailPanel";
import GlobalAgentWidget from "./components/GlobalAgentWidget";
import { Toaster } from "./components/ui/sonner";
import { Bell, Search, Menu, Key, AlertCircle, Clock, Loader2 } from "lucide-react";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import * as api from "./services/api";
import { hasPermission, canDelete } from "./utils/permissions";

function App() {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ 
    current_password: '', 
    new_password: '', 
    confirm_password: '' 
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Global search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');

  // Google OAuth callback state
  const [processingGoogleAuth, setProcessingGoogleAuth] = useState(false);
  const [googleAuthMessage, setGoogleAuthMessage] = useState(null);

  // Puerto Rico clock
  const [prTime, setPrTime] = useState('');

  // Permission checker function
  const checkPermission = useCallback((permission) => {
    return hasPermission(user, permission, roles);
  }, [user, roles]);

  const checkCanDelete = useCallback((resourceType) => {
    return canDelete(user, resourceType, roles);
  }, [user, roles]);

  useEffect(() => {
    const updatePRTime = () => {
      const now = new Date();
      const prTimeStr = now.toLocaleString('es-PR', {
        timeZone: 'America/Puerto_Rico',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      setPrTime(prTimeStr);
    };
    
    updatePRTime();
    const interval = setInterval(updatePRTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Check for saved session and seed database
  useEffect(() => {
    const initApp = async () => {
      // Try to seed database on first load
      try {
        await api.seedDatabase();
      } catch (error) {
        // Database might already be seeded
        console.log('Database already initialized');
      }

      // Load roles for permission checking
      try {
        const rolesRes = await api.getRoles();
        setRoles(rolesRes.data || []);
      } catch (error) {
        console.log('Could not load roles');
      }

      const savedUser = localStorage.getItem('crmUser');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setInitialized(true);
    };

    initApp();
  }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      if (code && state && !processingGoogleAuth) {
        setProcessingGoogleAuth(true);
        try {
          const redirectUri = window.location.origin + window.location.pathname;
          const result = await api.processGoogleCallback(code, state, redirectUri);
          
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          setGoogleAuthMessage({
            type: 'success',
            text: `✅ Cuenta de Google conectada: ${result.data.google_email}`
          });
          
          // Auto-hide message after 5 seconds
          setTimeout(() => setGoogleAuthMessage(null), 5000);
          
          // Navigate to admin page to see the connection
          setActiveTab('admin');
        } catch (err) {
          console.error('Google OAuth callback error:', err);
          window.history.replaceState({}, document.title, window.location.pathname);
          setGoogleAuthMessage({
            type: 'error',
            text: '❌ Error conectando cuenta de Google: ' + (err.response?.data?.detail || err.message)
          });
          setTimeout(() => setGoogleAuthMessage(null), 8000);
        } finally {
          setProcessingGoogleAuth(false);
        }
      }
    };
    
    handleGoogleCallback();
  }, [processingGoogleAuth]);

  const handleLogin = async (userData) => {
    // User data comes from API via LoginPage
    setUser(userData);
    localStorage.setItem('crmUser', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('crmUser');
    localStorage.removeItem('rememberedUser');
  };

  const openPasswordModal = () => {
    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    setPasswordError('');
    setPasswordSuccess(false);
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);
    
    if (!passwordForm.current_password) {
      setPasswordError('La contraseña actual es requerida');
      return;
    }
    
    if (!passwordForm.new_password) {
      setPasswordError('La nueva contraseña es requerida');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    
    try {
      await api.changePassword(user.id, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        is_admin: false // User changing own password
      });
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowPasswordModal(false);
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Load notifications (pending tasks due soon - only for logged-in user)
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) return;
      try {
        // Get tasks assigned to the current user only
        const response = await api.getTasks(user.name);
        const tasks = response.data || [];
        const today = new Date();
        const threeDaysLater = new Date(today);
        threeDaysLater.setDate(today.getDate() + 3);
        
        // Filter tasks that are pending and due within 3 days
        const urgentTasks = tasks.filter(t => {
          if (t.status === 'Completada') return false;
          if (!t.due_date) return false;
          const dueDate = new Date(t.due_date);
          return dueDate <= threeDaysLater;
        }).map(t => ({
          id: t.id,
          type: 'task',
          title: t.title,
          message: `Vence: ${t.due_date}`,
          priority: new Date(t.due_date) < today ? 'overdue' : 'soon'
        }));
        
        setNotifications(urgentTasks);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };
    
    loadNotifications();
    // Refresh notifications every 5 minutes
    const interval = setInterval(loadNotifications, 300000);
    return () => clearInterval(interval);
  }, [user]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard currentUser={user} globalSearch={globalSearchQuery} />;
      case 'empresas':
        return <EmpresasPage currentUser={user} canDelete={checkCanDelete} globalSearch={globalSearchQuery} />;
      case 'tareas':
        return <TareasPage currentUser={user} canDelete={checkCanDelete} globalSearch={globalSearchQuery} />;
      case 'embudos':
        return <EmbudosPage currentUser={user} canDelete={checkCanDelete} globalSearch={globalSearchQuery} />;
      case 'evaluaciones':
        return <EvaluacionesPage currentUser={user} globalSearch={globalSearchQuery} />;
      case 'formularios':
        return <PublicFormsPage currentUser={user} globalSearch={globalSearchQuery} />;
      case 'marketing':
        return <MarketingPage currentUser={user} globalSearch={globalSearchQuery} />;
      case 'agents':
        return <AgentsPage currentUser={user} />;
      case 'automations':
        return <AutomationsPage currentUser={user} />;
      case 'google-tools':
        return <GoogleToolsPage currentUser={user} globalSearch={globalSearchQuery} />;
      case 'gmail':
        return <GmailPanel currentUser={user} />;
      case 'reportes':
        return <ReportsPage currentUser={user} globalSearch={globalSearchQuery} />;
      case 'admin':
        return <AdminPage currentUser={user} />;
      default:
        return <Dashboard currentUser={user} />;
    }
  };

  const getPageTitle = () => {
    const titles = {
      dashboard: 'Panel de Control',
      empresas: 'Empresas',
      tareas: 'Tareas',
      embudos: 'Embudos',
      evaluaciones: 'Evaluaciones de Negocios Únicos',
      formularios: 'Formularios Públicos',
      marketing: 'Marketing & Social Media',
      agents: 'Agentes de IA',
      automations: 'Automatizaciones',
      'google-tools': 'Google Tools',
      gmail: 'Gmail Panel',
      reportes: 'Reportes',
      admin: 'Admin'
    };
    return titles[activeTab] || 'Panel de Control';
  };

  // Check if we're on a public form route
  const path = window.location.pathname;
  const publicFormMatch = path.match(/^\/f\/(.+)$/);
  if (publicFormMatch) {
    return <PublicFormRenderer formId={publicFormMatch[1]} />;
  }

  if (!initialized) {
    return (
      <div className="min-h-screen bg-[#e8e8e8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex">
      {/* Google OAuth Processing Overlay */}
      {processingGoogleAuth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="animate-spin text-[#D4A843]" size={24} />
            <span className="text-gray-700">Conectando cuenta de Google...</span>
          </div>
        </div>
      )}

      {/* Google OAuth Message */}
      {googleAuthMessage && (
        <div className={`fixed top-4 right-4 z-[100] p-4 rounded-lg shadow-lg max-w-md ${
          googleAuthMessage.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {googleAuthMessage.text}
        </div>
      )}

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed lg:static z-50 h-screen transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab);
            setMobileMenuOpen(false);
          }}
          user={user}
          onLogout={handleLogout}
          onChangePassword={openPasswordModal}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 hidden sm:block">{getPageTitle()}</h2>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder={`Buscar en ${getPageTitle().toLowerCase()}...`}
                className="pl-10 w-64 h-9 bg-gray-50 border-gray-200"
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setGlobalSearchQuery('');
                  }
                }}
                data-testid="global-search-input"
              />
              {globalSearchQuery && (
                <button
                  onClick={() => setGlobalSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>

            {/* Puerto Rico Time */}
            <div className="hidden sm:flex items-center space-x-1 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
              <Clock size={14} />
              <span>PR: {prTime}</span>
            </div>

            {/* Notifications */}
            <div className="relative">
              <button 
                className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} className="text-gray-600" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                    {notifications.length > 9 ? '9+' : notifications.length}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="font-medium text-gray-800">Notificaciones</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No hay notificaciones
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div 
                          key={notif.id} 
                          className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${
                            notif.priority === 'overdue' ? 'bg-red-50' : ''
                          }`}
                          onClick={() => {
                            setActiveTab('tareas');
                            setShowNotifications(false);
                          }}
                        >
                          <p className="text-sm font-medium text-gray-800">{notif.title}</p>
                          <p className={`text-xs ${notif.priority === 'overdue' ? 'text-red-600' : 'text-gray-500'}`}>
                            {notif.priority === 'overdue' ? '⚠️ Vencida - ' : '🕐 '}{notif.message}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <button 
                        className="w-full text-center text-sm text-[#D4A843] hover:text-[#c49a3a]"
                        onClick={() => {
                          setActiveTab('tareas');
                          setShowNotifications(false);
                        }}
                      >
                        Ver todas las tareas
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-[#D4A843] flex items-center justify-center text-white text-sm font-medium">
                {user.avatar || user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-800">{user.name}</p>
                <p className="text-xs text-gray-500">{user.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {renderContent()}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 px-6 py-3 text-center text-xs text-gray-500">
          © 2025 Infinito CRM - Infinito Television Puerto Rico. Todos los derechos reservados.
        </footer>
      </div>

      {/* Change Password Modal (for current user) */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key size={20} className="text-yellow-600" />
              Cambiar Mi Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Usuario:</p>
              <p className="font-medium text-gray-800">{user?.name}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña Actual *
              </label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Ingrese su contraseña actual"
                data-testid="current-password-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nueva Contraseña *
              </label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Nueva Contraseña *
              </label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Repetir nueva contraseña"
                data-testid="confirm-password-input"
              />
            </div>
            
            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle size={16} className="text-red-500" />
                <span className="text-sm text-red-600">{passwordError}</span>
              </div>
            )}
            
            {passwordSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-sm text-green-600">✓ Contraseña actualizada correctamente</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleChangePassword} 
              className="bg-yellow-600 hover:bg-yellow-700"
              disabled={passwordSuccess}
              data-testid="save-my-password-btn"
            >
              <Key size={16} className="mr-2" />
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Global Toast Notifications */}
      <Toaster richColors position="top-right" />

      {/* Global AI Agent Widget - Available on all pages */}
      <GlobalAgentWidget currentUser={user} activeTab={activeTab} />
    </div>
  );
}

export default App;
